import React, { useState, useEffect } from "react";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ErrorIcon from "@mui/icons-material/Error";
import WarningIcon from "@mui/icons-material/Warning";
import InfoIcon from "@mui/icons-material/Info";
import CloseIcon from "@mui/icons-material/Close";
import "./Toast.css";

export type ToastSeverity = "success" | "error" | "info" | "warning";

export interface ToastEvent {
  id: string;
  message: string;
  severity: ToastSeverity;
  duration: number;
}

let toastListener: ((event: ToastEvent) => void) | null = null;

/**
 * Global function to trigger a toast notification from anywhere in the application.
 * @param message The message to display in the toast.
 * @param severity The severity level ('success' | 'error' | 'info' | 'warning'). Defaults to 'success'.
 * @param duration The duration in milliseconds before auto-dismissing. Defaults to 4000ms.
 */
export const showToast = (
  message: string,
  severity: ToastSeverity = "success",
  duration = 4000
) => {
  if (toastListener) {
    toastListener({
      id: Math.random().toString(36).substring(2, 9),
      message,
      severity,
      duration,
    });
  }
};

export const Toast: React.FC = () => {
  const [toast, setToast] = useState<ToastEvent | null>(null);
  const [isHiding, setIsHiding] = useState(false);

  useEffect(() => {
    toastListener = (event) => {
      setIsHiding(false);
      setToast(event);
    };
    return () => {
      toastListener = null;
    };
  }, []);

  useEffect(() => {
    if (!toast) return;

    // Set a timer to start the slide-out animation before dismissing
    const hideTimeout = setTimeout(() => {
      setIsHiding(true);
    }, toast.duration - 250); // Start hiding 250ms early to sync with slide-out animation

    // Set a timer to fully remove the toast from the state
    const dismissTimeout = setTimeout(() => {
      setToast(null);
      setIsHiding(false);
    }, toast.duration);

    return () => {
      clearTimeout(hideTimeout);
      clearTimeout(dismissTimeout);
    };
  }, [toast]);

  if (!toast) return null;

  const handleClose = () => {
    setIsHiding(true);
    setTimeout(() => {
      setToast(null);
      setIsHiding(false);
    }, 250); // Match slide-out animation duration
  };

  const getIcon = () => {
    switch (toast.severity) {
      case "success":
        return <CheckCircleIcon fontSize="medium" />;
      case "error":
        return <ErrorIcon fontSize="medium" />;
      case "warning":
        return <WarningIcon fontSize="medium" />;
      case "info":
        return <InfoIcon fontSize="medium" />;
    }
  };

  return (
    <div className="toast-root-container">
      <div
        key={toast.id}
        className={`toast-card ${isHiding ? "toast-hiding" : ""}`}
        style={{ "--duration": `${toast.duration}ms` } as React.CSSProperties}
      >
        {/* Color accent bar on the left */}
        <div className={`toast-accent ${toast.severity}`} />

        {/* Severity Icon */}
        <div className={`toast-icon ${toast.severity}`}>{getIcon()}</div>

        {/* Message Content */}
        <div className="toast-content">{toast.message}</div>

        {/* Close Button */}
        <button className="toast-close-btn" onClick={handleClose} aria-label="Close notification">
          <CloseIcon fontSize="small" />
        </button>

        {/* Dynamic shrinking progress bar */}
        <div className={`toast-progress ${toast.severity}`} />
      </div>
    </div>
  );
};
